/* eslint-disable unicorn/filename-case */
import { useAuth0 } from '@auth0/auth0-react';
import { useUnleashContext } from '@alchemer/unleash';
import React, { useEffect, useRef } from 'react';
import type { AppAuthContext } from '@/types/auth';
import { tokenStorage } from '@/lib/tokenStorage';

interface AuthProviderProps {
  children: (auth: AppAuthContext) => React.ReactNode;
}

/**
 * A wrapper component that handles authentication using Auth0.
 * It redirects unauthenticated users to the login page and provides
 * authentication context to its children.
 *
 * @param children - A render prop that receives the authentication context.
 * @returns A React element that renders the children with authentication context.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const sdk = useAuth0();
  const updateUnleashContext = useUnleashContext();
  const triggeredRef = useRef(false);

  useEffect(() => {
    if (!sdk.isAuthenticated && !sdk.isLoading && !triggeredRef.current) {
      triggeredRef.current = true;
      const returnTo = window.location.pathname + window.location.search + window.location.hash;
      void sdk.loginWithRedirect({ appState: { returnTo } });
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [sdk.isAuthenticated, sdk.isLoading, sdk.loginWithRedirect]);

  // Cleanup effect when user becomes unauthenticated
  useEffect(() => {
    if (!sdk.isAuthenticated && !sdk.isLoading) {
      tokenStorage.clearToken();

      if (!import.meta.env.PROD) {
        console.log('[AuthProvider] Cleared token due to unauthenticated state');
      }
    }
  }, [sdk.isAuthenticated, sdk.isLoading]);

  // Clear Unleash context on logout — authenticated context is managed by useUnleashUserContext
  useEffect(() => {
    if (!sdk.isAuthenticated && !sdk.isLoading) {
      updateUnleashContext({ userId: 'anonymous', properties: {} });
    }
  }, [sdk.isAuthenticated, sdk.isLoading, updateUnleashContext]);

  const auth: AppAuthContext = {
    isAuthenticated: sdk.isAuthenticated,
    isLoading: sdk.isLoading,
    user: sdk.user as any,
    login: (options) => sdk.loginWithRedirect(options),
    loginWithRedirect: sdk.loginWithRedirect,
    logout: (options) => sdk.logout(options),
    getAccessTokenSilently: sdk.getAccessTokenSilently,
    getAccessTokenWithPopup: sdk.getAccessTokenWithPopup,
    getIdTokenClaims: sdk.getIdTokenClaims,
    loginWithPopup: sdk.loginWithPopup,
  };

  return <>{children(auth)}</>;
};
